import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * @typedef Props
 * @prop {string} [value]
 * @prop {boolean} [required]
 * @prop {(x: string) => void} onChange
 * @prop {boolean} [disabled]
 * @prop {'number' | 'text'} [type]
 * @prop {boolean} [disabled]
 * @prop {boolean} [password]
 * @prop {boolean} [invalid]
 * @prop {boolean} [clear]
 * @prop {() => void} onBackspace
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
/**
 * @type React.FC<Props>
 */
const MotifPinItem = forwardRef((props, ref) => {
  const {
    value = '',
    required = false,
    onChange,
    type = 'number',
    disabled = false,
    password = false,
    invalid = false,
    onBackspace,
    clear = false,
    ...other
  } = props;

  const classes = classNames({
    'motif-small-input-item': true,
    'motif-small-input-item-invalid': invalid
  });

  const [state, setState] = useState({ value, focus: false });
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current.focus()
  }));

  useEffect(() => {
    if (clear) {
      setState({
        value: ''
      });
    }
  }, [clear]);

  const onKeyDown = event => {
    if (event.keyCode === 8 && (!state.value || !state.value.length)) {
      onBackspace();
    }
  };

  const handleOnChange = event => {
    const inputValue = validate(event.target.value);
    if (state.value !== inputValue && inputValue.length < 2) {
      setState(prevState => ({
        ...prevState,
        value: inputValue
      }));

      setTimeout(() => onChange(inputValue), 0);
    }
  };

  const onFocus = event => {
    event.target.select();
    setState(prevState => ({
      ...prevState,
      focus: true
    }));
  };

  const onBlur = () => {
    setState(prevState => ({
      ...prevState,
      focus: false
    }));
  };

  const validate = val => {
    if (type === 'number') {
      const numCode = val.charCodeAt(0);
      const isInteger = numCode >= '0'.charCodeAt(0) && numCode <= '9'.charCodeAt(0);

      return isInteger ? val : '';
    }

    return val.toUpperCase();
  };

  const getPattern = () => {
    if (type === 'number') {
      return '[0-9]*';
    }

    return '[A-Z0-9]*';
  };

  return (
    <input
      {...other}
      value={state.value || ''}
      disabled={disabled}
      ref={inputRef}
      required={required}
      onChange={handleOnChange}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      type={password ? 'password' : 'text'}
      pattern={getPattern()}
      className={classes}
      maxLength={1}
      autoComplete="off"
      inputMode="text"
    />
  );
});

MotifPinItem.propTypes = {
  type: PropTypes.oneOf(['number', 'text']),
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBackspace: PropTypes.func.isRequired,
  password: PropTypes.bool,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  clear: PropTypes.bool
};

export { MotifPinItem };
